import Information16 from '@carbon/icons-react/es/information/16';
import { Tooltip, Typography } from 'antd';
import moment from 'moment';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Document as DocumentApi, Document } from 'api/document/types/document';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import { humanise } from 'components/atoms/humanised-date';
import { IconWrapper } from 'components/atoms/icon-shell/icon-shell';
import { RelativeTime } from 'components/atoms/relative-time';
import { EditorModes } from 'data/documents';
import { useStoreMembers } from 'data/members/hooks/use-store-members';
import { useDistractionFreeMode } from 'hooks/use-distraction-free-mode';
import { useProjectWSUpdates } from 'hooks/use-project-ws-updates';
import { fullName } from 'utils/full-name';
import styles from './last-updated-by.module.css';

const { Paragraph } = Typography;

const StyledParagraph = styled(Paragraph)`
  color: var(--text-02);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-caption-01);
`;

interface Props {
  projectId: number;
  documentId: number;
  document: Document;
}

const getSection = (doc: Document, type: EditorModes) =>
  doc.sections.find((section) => section.meta.sectionType === type);

export const LastUpdatedBy: FC<Props> = ({
  projectId,
  documentId,
  document,
}) => {
  const publicSec = getSection(document, EditorModes.Public);
  const privateSec = getSection(document, EditorModes.Private);

  const latestUpdatedSection =
    privateSec && publicSec && publicSec?.updatedAt < privateSec?.updatedAt
      ? privateSec
      : publicSec;

  const [userId, setUserId] = useState<null | number>(
    latestUpdatedSection?.lastUpdatedBy || null,
  );
  const [createdBy, setCreator] = useState<null | number>(
    latestUpdatedSection?.createdBy || null,
  );
  const [time, setTime] = useState<null | number>(
    latestUpdatedSection?.updatedAt || null,
  );
  const [creationTime, setCreatedTime] = useState<null | number>(
    latestUpdatedSection?.createdAt || null,
  );

  const { users } = useStoreMembers();
  const user = users?.find((userObj) => userObj.userId === userId);
  const creator = users?.find((userObj) => userObj.userId === createdBy);

  const handleSocketUpdate = useCallback(
    (socketData: SocketData<DocumentApi>) => {
      if (socketData.data.documentId === documentId) {
        const { data } = socketData;

        const publicSection = getSection(data, EditorModes.Public);

        const privateSection = getSection(data, EditorModes.Private);

        if (publicSection) {
          const section =
            privateSection &&
            publicSection?.updatedAt < privateSection?.updatedAt
              ? privateSection
              : publicSection;

          setUserId(section.lastUpdatedBy);
          setTime(section.updatedAt);
          if (!createdBy) {
            setCreator(publicSection.createdBy);
            setCreatedTime(publicSection.createdAt);
          }
        }
      }
    },
    [createdBy, documentId],
  );

  useProjectWSUpdates(
    projectId,
    handleSocketUpdate,
    EventType(Action.Update, ObjectType.Document),
  );

  const UpdatedBy = time ? (
    <p className="text-02 hint">
      <FormattedMessage
        id="document.last_updated_by"
        values={{
          user: user ? fullName(user) : '',
          time: <RelativeTime time={moment(time)} />,
        }}
      />
    </p>
  ) : null;

  const { active: isDistractionFree } = useDistractionFreeMode();

  return user ? (
    <div className={styles.content}>
      <Tooltip
        title={
          <div className={styles.content__tooltip}>
            {UpdatedBy}
            <p className="hint">
              <FormattedMessage
                id="document.created_by"
                values={{
                  user: creator ? fullName(creator) : '',
                  time: humanise(moment(creationTime)),
                }}
              />
            </p>
          </div>
        }
        placement="bottom"
      >
        {isDistractionFree ? (
          <StyledParagraph ellipsis={{ rows: 1 }}>
            <FormattedMessage
              id="document.last_updated_by"
              values={{
                user: user ? fullName(user) : '',
                time: <RelativeTime time={moment(time)} />,
              }}
            />
          </StyledParagraph>
        ) : (
          <IconWrapper>
            <Information16 />
          </IconWrapper>
        )}
      </Tooltip>
    </div>
  ) : (
    <div />
  );
};
